import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Select,
  Spinner,
  Alert,
  Chip,
  Tabs,
  Button,
  TextField,
  EmptyState,
} from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import {
  adminApiClient,
  AdminApiError,
  type SupportRequestItem,
  type SupportStatus,
} from '../lib/adminApiClient';
import { NEUTRAL_BTN } from '../lib/buttonStyles';

/**
 * Support / contact-request inbox (support:read to view, support:resolve to
 * act). Reads the public /contact submissions (tblcontact): triage by
 * open/resolved, reply (answer), resolve, reopen. Each action is audited.
 */

// Non-empty sentinel for the "All topics" option (empty-string values leave the
// DSL Select's floating label un-shrunk → it overlaps the value text).
const ALL_TOPICS = '__all__';

function Centered({ children }: { children: React.ReactNode }) {
  return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>{children}</Box>;
}

export default function SupportRequests() {
  const caps = useCapabilities();
  const canRead = caps.has('support:read');
  const canResolve = caps.has('support:resolve');

  const [status, setStatus] = useState<SupportStatus>('open');
  const [topicFilter, setTopicFilter] = useState(ALL_TOPICS);
  const [items, setItems] = useState<SupportRequestItem[]>([]);
  const [topics, setTopics] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listSupportRequests({
        status,
        topic: topicFilter === ALL_TOPICS ? undefined : topicFilter,
      });
      setItems(res.items);
      setTopics(res.topics);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load support requests');
    } finally {
      setLoading(false);
    }
  }, [status, topicFilter]);

  useEffect(() => {
    if (canRead) void load();
  }, [canRead, load]);

  if (caps.loading)
    return (
      <Centered>
        <Spinner color="brand" />
      </Centered>
    );
  if (!canRead)
    return <Alert severity="error">You don’t have permission to view support requests.</Alert>;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Support requests
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Messages submitted through the public contact form.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Tabs
          ariaLabel="Support request status"
          value={status}
          onChange={(_e, v) => setStatus(v as SupportStatus)}
          tabs={[
            { value: 'open', label: 'Open' },
            { value: 'resolved', label: 'Resolved' },
            { value: 'all', label: 'All' },
          ]}
        />
        <Box sx={{ flex: 1 }} />
        <Select
          ariaLabel="Filter by topic"
          label="Topic"
          native
          size="small"
          sx={{ width: 200 }}
          value={topicFilter}
          onChange={(_e, v) => setTopicFilter(v as string)}
          options={[{ value: ALL_TOPICS, label: 'All topics' }, ...topics.map((t) => ({ value: t, label: t }))]}
        />
      </Box>

      {flash && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={flash.kind}>{flash.msg}</Alert>
        </Box>
      )}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading ? (
        <Centered>
          <Spinner color="brand" />
        </Centered>
      ) : items.length === 0 ? (
        <EmptyState icon="messages" title="Inbox zero" body="No support requests match this filter." />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {items.map((r) => (
            <RequestRow
              key={r.id}
              req={r}
              canResolve={canResolve}
              onDone={(kind, msg) => {
                setFlash({ kind, msg });
                void load();
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

function RequestRow({
  req,
  canResolve,
  onDone,
}: {
  req: SupportRequestItem;
  canResolve: boolean;
  onDone: (kind: 'success' | 'error', msg: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [answer, setAnswer] = useState(req.answer ?? '');
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      onDone('success', ok);
    } catch (err) {
      onDone('error', err instanceof AdminApiError ? err.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const topicLabel = req.topic === 'Other' && req.topicOther ? `Other — ${req.topicOther}` : req.topic;

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
            <Chip label={topicLabel} size="small" color="primary" />
            <Chip
              label={req.isResolved ? 'Resolved' : 'Open'}
              size="small"
              color={req.isResolved ? 'success' : 'warning'}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {req.submittedEmail} · {new Date(req.submittedAt).toLocaleDateString()}
            </Typography>
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {req.question}
          </Typography>
          {req.answer && !open && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              <strong>Reply:</strong> {req.answer}
            </Typography>
          )}
          {req.answeredBy && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
              {req.isResolved ? 'Resolved' : 'Last answered'} by {req.answeredBy}
              {req.answeredAt ? ` · ${new Date(req.answeredAt).toLocaleDateString()}` : ''}
            </Typography>
          )}
          {req.githubIssueUrl && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              <a href={req.githubIssueUrl} target="_blank" rel="noreferrer">
                GitHub issue #{req.githubIssueNumber}
              </a>
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {canResolve && (
            <Button
              ariaLabel={open ? 'Hide reply box' : 'Reply'}
              buttonText={open ? 'Close' : 'Reply'}
              variant="outlined"
              size="small"
              sx={NEUTRAL_BTN}
              onClick={() => setOpen((v) => !v)}
              disabled={busy}
            />
          )}
          {canResolve &&
            (req.isResolved ? (
              <Button
                ariaLabel="Reopen request"
                buttonText="Reopen"
                variant="outlined"
                size="small"
                sx={NEUTRAL_BTN}
                onClick={() => run(() => adminApiClient.reopenSupportRequest(req.id), 'Reopened.')}
                disabled={busy}
              />
            ) : (
              <Button
                ariaLabel="Mark resolved"
                buttonText="Resolve"
                variant="contained"
                size="small"
                onClick={() => run(() => adminApiClient.resolveSupportRequest(req.id), 'Marked resolved.')}
                disabled={busy}
              />
            ))}
        </Box>
      </Box>

      {open && canResolve && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            label="Reply"
            ariaLabel="Reply to support request"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            disabled={busy}
            fullWidth
            multiline
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              ariaLabel="Save reply"
              buttonText="Save reply"
              variant="outlined"
              size="small"
              sx={NEUTRAL_BTN}
              onClick={() =>
                run(
                  () => adminApiClient.answerSupportRequest(req.id, answer.trim(), false),
                  'Reply saved.',
                )
              }
              disabled={busy || !answer.trim()}
            />
            <Button
              ariaLabel="Reply and resolve"
              buttonText="Reply & resolve"
              variant="contained"
              size="small"
              onClick={() =>
                run(
                  () => adminApiClient.answerSupportRequest(req.id, answer.trim(), true),
                  'Replied & resolved.',
                )
              }
              disabled={busy || !answer.trim()}
            />
          </Box>
        </Box>
      )}
    </Paper>
  );
}
